import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { com } from '@eidu/web'
import useEntityTypes from '../../io/useEntityTypes'
import useEntityType from '../../io/useEntityType'
import { fetchEntitiesByIds, getOrFetchTypes } from '../../io/useEntities'
import { entityKeys } from '../../io/queryKeys'
import DraftFieldValue from '../../domain/entity/DraftFieldValue'
import getNonNullFieldsFromEntity from '../../util/entity/getNonNullFieldsFromEntity'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityId = com.eidu.sharedlib.entity.EntityId
import FieldId = com.eidu.sharedlib.entity.field.FieldId

type UseEntityParams = {
  entityTypeId: EntityTypeId
  entityId: EntityId | undefined
}

export type UseEntityResult = {
  entityType: EntityType | undefined
  entityTypes: ReadonlyMap<EntityTypeId, EntityType> | undefined
  entity: EntityWithLabelAndRelated | undefined
  fields: ReadonlyMap<FieldId, DraftFieldValue>
  loading: boolean
  error: string | undefined
}

const useEntity = ({ entityTypeId, entityId }: UseEntityParams): UseEntityResult => {
  const queryClient = useQueryClient()
  const { entityTypes, isLoading: typesLoading, error: typesError } = useEntityTypes()
  const { entityType, isLoading: typeLoading, error: typeError } = useEntityType(entityTypeId)
  const composedError = typesError ?? typeError
  const errorString = composedError ? String(composedError) : undefined
  const repositoryLoading = typesLoading || typeLoading

  const entityQuery = useQuery({
    queryKey: entityId ? entityKeys.detail(entityId) : ['_no_entity'],
    queryFn: async () => {
      const types = await getOrFetchTypes(queryClient)
      const result = await fetchEntitiesByIds([entityId!], types)
      return result.get(entityId!) ?? null
    },
    enabled: !!entityId && !!entityType && !!entityTypes,
  })

  const entity = entityId ? entityQuery.data : undefined

  const entityNotFound = entityId !== undefined && entity === null
  const error = errorString ?? (entityNotFound ? 'Entity not found' : undefined)
  const loading = (repositoryLoading || (entityId !== undefined && entity === undefined)) && !error

  const [fields, setFields] = useState<ReadonlyMap<FieldId, DraftFieldValue>>(new Map())
  useEffect(() => {
    if (entity) setFields(getNonNullFieldsFromEntity(entity.entity))
  }, [entity])

  return {
    entityType: entityType ?? undefined,
    entityTypes,
    entity: entity ?? undefined,
    fields,
    loading,
    error,
  }
}

export default useEntity
